import { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';

/**
 * Configuration for a single notification
 */
export type DataGridNotificationMessage = {
	/**
	 * Title of the notification
	 */
	title: string;
	/**
	 * Content to display in the notification
	 */
	content?: ReactNode;
	/**
	 * Optional timeout in milliseconds - if provided, notification will auto-dismiss
	 */
	timeout?: number;
	/**
	 * Whether the notification can be dismissed by the user
	 */
	undismissable?: boolean;
	/**
	 * Severity of the notification - affects styling
	 * Default is 'info', can be 'success', 'warning', or 'error'
	 */
	severity?: 'info' | 'success' | 'warning' | 'error';
	/**
	 * Optional progress indicator - can be a number (0-100) or true for indeterminate
	 */
	progress?: number | boolean;
	/**
	 * Optional callback when notification is dismissed
	 */
	onDismiss?: () => void;
};

/**
 * Internal state for the notification (consumers don't need to know about this)
 */
type DataGridNotificationState = {
	/**
	 * Whether the notification is minimized
	 */
	minimized: boolean;
	/**
	 * Current notification message
	 */
	message: DataGridNotificationMessage | null;
	// Note: don't be tempted to use the _presence of a notification_ in place of a dedicated `dissmissed` property.
	// I went down said path resulting in syncing local state in order to animate out messages that no-longer exits.
	/**
	 * Whether the current message has been dismissed (for exit animation)
	 */
	dismissed: boolean;
};

/**
 * Actions for consumers - just send notifications!
 */
export type DataGridNotificationActions = {
	/**
	 * Send a notification
	 */
	sendNotification: (message: DataGridNotificationMessage) => void;
	/**
	 * Clear any current notification
	 */
	clearNotification: () => void;
};

/**
 * Internal actions (for the notification component itself)
 */
type DataGridNotificationInternalActions = DataGridNotificationActions & {
	/**
	 * Set the minimized state (internal use)
	 */
	setMinimized: (minimized: boolean | ((prev: boolean) => boolean)) => void;
	/**
	 * Clear notification (internal use - handles dismiss callback)
	 */
	dismiss: () => void;
	/**
	 * Current state (internal use)
	 */
	state: DataGridNotificationState;
};

/**
 * Context type for consumers
 */
export type DataGridNotificationContextType = DataGridNotificationActions;

/**
 * Context type for internal use by the notification component
 */
export type InternalNotificationContextType = DataGridNotificationInternalActions;

/**
 * Context for notification actions
 */
const NotificationContext = createContext<InternalNotificationContextType | null>(null);

const MINIMUM_DISPLAY_TIME = 1000;

/**
 * Provider component for notification context
 */
export const DataGridNotificationProvider = ({ children }: { children: ReactNode }) => {
	const [minimized, setMinimized] = useState(false);
	const [message, setMessage] = useState<DataGridNotificationMessage | null>(null);
	const [dismissed, setDismissed] = useState(false);

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const minDisplayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastNotificationTime = useRef<number>(0);
	const pendingNotification = useRef<DataGridNotificationMessage | null>(null);

	const sendNotification = (newMessage: DataGridNotificationMessage) => {
		const now = Date.now();
		const timeSinceLastNotification = now - lastNotificationTime.current;

		// If we have a current message and it hasn't been shown for the minimum time
		if (message && timeSinceLastNotification < MINIMUM_DISPLAY_TIME) {
			// Store the pending notification and wait
			pendingNotification.current = newMessage;

			// Clear any existing minimum display timeout
			if (minDisplayTimeoutRef.current) {
				clearTimeout(minDisplayTimeoutRef.current);
			}

			// Set a timeout to show the pending notification after minimum time
			minDisplayTimeoutRef.current = setTimeout(() => {
				if (pendingNotification.current) {
					const pendingMsg = pendingNotification.current;
					pendingNotification.current = null;
					sendNotification(pendingMsg);
				}
			}, MINIMUM_DISPLAY_TIME - timeSinceLastNotification);

			return;
		}

		// Clear any existing timeouts
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		if (minDisplayTimeoutRef.current) {
			clearTimeout(minDisplayTimeoutRef.current);
			minDisplayTimeoutRef.current = null;
		}

		// Clear any pending notification since we're showing this one now
		pendingNotification.current = null;

		setMessage(newMessage);
		setMinimized(false); // Show notification when new message arrives
		setDismissed(false); // Reset dismissed state for new message
		lastNotificationTime.current = now;

		// Set timeout if provided
		if (newMessage.timeout) {
			timeoutRef.current = setTimeout(() => {
				dismiss();
			}, newMessage.timeout);
		}
	};

	const clearNotification = () => {
		const now = Date.now();
		const timeSinceLastNotification = now - lastNotificationTime.current;

		// If minimum display time hasn't passed, delay the clear
		if (message && timeSinceLastNotification < MINIMUM_DISPLAY_TIME) {
			if (minDisplayTimeoutRef.current) {
				clearTimeout(minDisplayTimeoutRef.current);
			}
			minDisplayTimeoutRef.current = setTimeout(() => {
				clearNotification();
			}, MINIMUM_DISPLAY_TIME - timeSinceLastNotification);
			return;
		}

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		if (minDisplayTimeoutRef.current) {
			clearTimeout(minDisplayTimeoutRef.current);
			minDisplayTimeoutRef.current = null;
		}
		setMessage(null);
	};

	const dismiss = () => {
		const currentMessage = message;
		setDismissed(true); // Mark as dismissed to trigger exit animation

		// After animation time, actually clear the message
		setTimeout(() => {
			clearNotification();
		}, 300); // Match the Slide transition timeout

		// Call dismiss callback if provided
		if (currentMessage?.onDismiss) {
			currentMessage.onDismiss();
		}
	};

	// Clean up timeout on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			if (minDisplayTimeoutRef.current) {
				clearTimeout(minDisplayTimeoutRef.current);
			}
		};
	}, []);

	const contextValue: InternalNotificationContextType = {
		// Public actions
		sendNotification,
		clearNotification,
		// Internal actions
		setMinimized,
		dismiss,
		// Internal state
		state: {
			minimized,
			message,
			dismissed
		}
	};

	return <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>;
};

/**
 * Hook for consumers - simple notification sending
 */
export const useNotification = (): DataGridNotificationContextType | null => {
	const context = useContext(NotificationContext);
	if (!context) return null;

	return {
		sendNotification: context.sendNotification,
		clearNotification: context.clearNotification
	};
};

/**
 * Hook for internal use by the notification component
 */
export const useInternalNotification = (): InternalNotificationContextType | null => {
	return useContext(NotificationContext);
};
